/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: TelephonyServersPage.tsx
 ??? Created: 2025-01-05
 ?? Description:
    Main telephony servers management page with data grid, CRUD operations,
    and comprehensive server monitoring capabilities.

 ?? Notes:
    - Integrates DataGridSimple for server listing and management
    - Supports server creation, editing, deletion, and monitoring
    - Implements search and filtering functionality
    - Uses confirmation modals for destructive actions
    - Integrates with FreeSWITCH telephony systems

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server } from 'lucide-react';

import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../store/auth';
import { useNavigationStore } from '../../store/navigation';
import { deleteTelephonyServer, verifyTelephonyServer } from './api';
import { useTelephonyServersList } from './hooks';
import { TelephonyServersActions } from './components';
import { TelephonyServer } from './types';

export default function TelephonyServersPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { setCurrentPageTitle } = useNavigationStore();
  const { showError, showSuccess } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TelephonyServer | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  const tenantSlug = user?.tenant?.slug ?? t('telephonyServers.defaultTenant');
  const userId = user?.id;

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useTelephonyServersList();

  const handleCreate = useCallback(() => {
    navigate('/admin/system-settings/telephony-servers/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (server: TelephonyServer) => {
      navigate(`/admin/system-settings/telephony-servers/${server.uuid ?? server.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (server: TelephonyServer) => {
      navigate(`/admin/system-settings/telephony-servers/new?duplicate=${server.uuid ?? server.id}`);
    },
    [navigate]
  );

  const handleDelete = useCallback((server: TelephonyServer) => {
    setPendingDelete(server);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;

    try {
      setIsDeleting(true);
      await deleteTelephonyServer(pendingDelete.uuid, tenantSlug, userId);
      showSuccess(t('telephonyServers.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setPendingDelete(null);
      refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('telephonyServers.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [pendingDelete, refresh, showError, showSuccess, t, tenantSlug, userId]);

  const handleCheckStatus = useCallback(
    async (server?: TelephonyServer) => {
      const targets = server ? [server] : data;
      if (!targets.length) return;

      try {
        setIsCheckingStatus(true);

        await Promise.all(
          targets.map(async (item) => {
            await verifyTelephonyServer(item.id, tenantSlug, userId);
          }),
        );

        await refresh();
        showSuccess(t('telephonyServers.messages.statusUpdated'));
      } catch (error) {
        showError(
          error instanceof Error
            ? error.message
            : t('telephonyServers.messages.statusUpdateFailed'),
        );
      } finally {
        setIsCheckingStatus(false);
      }
    },
    [data, refresh, showError, showSuccess, t, tenantSlug, userId],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            title={t('telephonyServers.servers')}
            titleIcon={<Server className="h-5 w-5 text-primary" />}
            data={data}
            columns={columns}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onSearch={handleSearch}
            onColumnToggle={handleColumnToggle}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onAdd={handleCreate}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            deleteMessage={(server?: TelephonyServer) =>
              server?.name
                ? t('telephonyServers.confirmDeleteWithName', { name: server.name })
                : t('telephonyServers.confirmDelete')
            }
            renderActions={(server: TelephonyServer) => (
              <TelephonyServersActions
                item={server}
                isCheckingStatus={isCheckingStatus}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                onCheckStatus={handleCheckStatus}
              />
            )}
            addButtonText={t('common.add')}
            emptyMessage={t('telephonyServers.noServersFound')}
            searchPlaceholder={t('telephonyServers.searchServers')}
            deleteTitle={t('telephonyServers.deleteServerTitle')}
            translations={{
              items: t('common.items'),
              columns: t('common.columns'),
              noItemsFound: t('common.noItemsFound'),
              showing: t('common.showing'),
              to: t('common.to'),
              of: t('common.of'),
              results: t('common.results'),
              previous: t('common.previous'),
              next: t('common.next'),
              page: t('common.page'),
              deleteItem: t('common.deleteItem'),
              areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
              delete: t('common.delete'),
              cancel: t('common.cancel'),
              allSelected: t('common.allSelected'),
              selected: t('common.selected'),
              searchOptions: t('common.searchOptions'),
              noOptionsFound: t('common.noOptionsFound'),
              selectAll: t('common.selectAll'),
              clearAll: t('common.clearAll')
            }}
          />

          <ConfirmationModal
            type="danger"
            isOpen={showDeleteModal}
            isLoading={isDeleting}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title={t('telephonyServers.deleteServerTitle')}
            message={
              pendingDelete
                ? t('telephonyServers.confirmDeleteWithName', { name: pendingDelete.name })
                : t('telephonyServers.confirmDelete')
            }
            confirmText={t('common.delete')}
            cancelText={t('common.cancel')}
          />
        </>
      }
      formView={null}
    />
  );
}
